<template>
  <div>
    <container
      :isModal="true"
      @close-modal="cancelSelection()"
      :loading="!stateReady"
    >
      <template v-slot:header>
        <h3>Consulta de Veículos</h3>
      </template>
      <template v-slot:body>
        <div v-if="stateReady">
          <div class="rntrc-aviso rntrc-aviso--info p-mb-3">
            <i class="fas fa-info-circle p-mr-2" />
            Veículos do cadastro da ANTT e da GovPass da sua base. Marque as
            placas e confirme. Placa da GovPass precisa ser cadastrada na ANTT
            antes (botão <b>Cadastrar</b>, já preenchido).
          </div>
          <form @submit.prevent="submitSearch()">
            <fieldset :disabled="carregando">
              <div class="p-formgrid p-grid">
                <div class="p-field p-fluid p-col-12 p-md-6 p-lg-4">
                  <input-with-label label="Placa" v-slot="l">
                    <InputText
                      :id="l.id"
                      v-model="placa"
                      maxlength="8"
                      v-focus
                    />
                  </input-with-label>
                </div>
                <div class="p-col-12">
                  <button-group>
                    <Button
                      type="submit"
                      class="p-button-rounded"
                      label="Buscar"
                    />
                    <Button
                      type="button"
                      label="Limpar"
                      class="p-button-rounded p-button-text"
                      @click="clearFilter()"
                    />
                  </button-group>
                </div>
              </div>
            </fieldset>
          </form>
          <search-result
            :showSelector="true"
            :multiple="true"
            :results="resultados"
            :selected="[]"
            keyParam="placa"
            :loading="carregando"
            :hidePagination="true"
            :blockedSelection="blockedSelection"
            @confirm-selection="confirmSelection"
          >
            <template v-slot:title>
              <span class="search-result-title">Veículos encontrados</span>
            </template>
            <template v-slot:columns>
              <Column header="Placa">
                <template #body="{ data }">
                  <b v-text="formatPlaca(data.placa)" />
                </template>
              </Column>
              <Column header="Tipo">
                <template #body="{ data }">
                  {{ getServicoVeiculoLabel(data.flServicoVeiculo) }}
                </template>
              </Column>
              <Column field="renavam" header="Renavam" />
              <Column header="Proprietário">
                <template #body="{ data }">
                  <span v-text="getNomePessoaAntt(data.proprietario)" />
                  <span
                    v-if="outroProprietario && outroProprietario(data)"
                    class="aet-badge aet-badge--warning p-ml-2"
                    v-title="
                      'Na inclusão própria todas as placas precisam ser do mesmo proprietário'
                    "
                    >Outro proprietário</span
                  >
                </template>
              </Column>
              <Column header="CRLV">
                <template #body="{ data }">
                  <span
                    v-if="data.idVeiculoAntt"
                    class="aet-badge"
                    :class="
                      data.possuiCrlv
                        ? 'aet-badge--success'
                        : 'aet-badge--danger'
                    "
                    v-text="data.possuiCrlv ? 'Sim' : 'Não'"
                  />
                  <span
                    v-else
                    class="aet-badge"
                    :class="
                      data.possuiCrlvGovpass
                        ? 'aet-badge--info'
                        : 'aet-badge--danger'
                    "
                    v-text="data.possuiCrlvGovpass ? 'Na GovPass' : 'Não'"
                  />
                </template>
              </Column>
              <Column header="Cadastro">
                <template #body="{ data }">
                  <span
                    class="aet-badge"
                    :class="
                      data.idVeiculoAntt
                        ? 'aet-badge--success'
                        : 'aet-badge--info'
                    "
                    v-text="data.idVeiculoAntt ? 'ANTT' : 'GovPass'"
                  />
                </template>
              </Column>
              <Column style="width: 11rem; text-align: right">
                <template #body="{ data }">
                  <Button
                    v-if="!data.idVeiculoAntt"
                    type="button"
                    icon="pi pi-plus-circle"
                    label="Cadastrar"
                    class="p-button-rounded p-button-sm"
                    v-title="
                      'Cadastra na ANTT com os dados da GovPass (o CRLV é copiado)'
                    "
                    @click="abrirCadastro(null, data.placa)"
                  />
                  <Button
                    v-else
                    type="button"
                    icon="pi pi-pencil"
                    class="p-button-rounded p-button-text p-button-sm"
                    v-title="'Alterar o veículo (CRLV, proprietário...)'"
                    @click="abrirCadastro(data.idVeiculoAntt, null)"
                  />
                </template>
              </Column>
            </template>
            <template v-slot:actions>
              <Button
                type="button"
                icon="pi pi-plus-circle"
                label="Novo veículo"
                class="p-button-rounded p-button-outlined"
                @click="abrirCadastro(null, placa)"
              />
              <Button
                type="button"
                label="Cancelar"
                class="p-button-rounded p-button-text"
                @click="cancelSelection()"
              />
            </template>
          </search-result>
          <small
            class="rntrc-busca-limite"
            v-if="(resultados ?? []).length >= 20"
            >Mostrando os 20 primeiros: digite mais da placa para achar
            outro.</small
          >
        </div>
      </template>
    </container>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  PropType,
  Ref
} from "vue";
import { getNomePessoaAntt } from "../../../modules/business/rntrc/pessoa/pessoaAnttBusiness";
import { VeiculoAnttPayload } from "../../../modules/business/rntrc/veiculo/veiculoAnttBusiness";
import {
  formatPlaca,
  getServicoVeiculoLabel
} from "../../../modules/business/rntrc/rntrcTypes";

interface BuscaVeiculoAnttState {
  placa: string;
  stateReady: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

/**
 * Consulta de veículos da tela de serviço, em pop-up como a consulta de veículos da licença:
 * filtro de placa, seleção de várias placas e Confirmar. A busca (ANTT + GovPass do cliente) e
 * os resultados ficam na tela que abre; aqui só filtro, seleção e os atalhos de cadastro.
 */
export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<BuscaVeiculoAnttState>({
      placa: "",
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const submitSearch = () => {
      emit("buscar", state.placa);
    };

    const clearFilter = () => {
      state.placa = "";
      emit("buscar", "");
    };

    const confirmSelection = (selecionados: Array<VeiculoAnttPayload>) => {
      emit("confirm-selection", selecionados ?? []);
    };

    const cancelSelection = () => {
      emit("cancel-selection");
    };

    const abrirCadastro = (
      idVeiculoAntt: number | null,
      placa: string | null
    ) => {
      emit("abrir-cadastro", idVeiculoAntt, placa);
    };

    onMounted(() => {
      state.placa = props.placaInicial ?? "";
      state.stateReady = true;
    });

    return {
      submitSearch,
      clearFilter,
      confirmSelection,
      cancelSelection,
      abrirCadastro,
      formatPlaca,
      getServicoVeiculoLabel,
      getNomePessoaAntt,
      ...toRefs(state)
    };
  },
  props: {
    resultados: Array as PropType<Array<VeiculoAnttPayload>>,
    carregando: Boolean,
    placaInicial: String,
    /** Motivo que impede marcar a placa (já adicionada, sem CRLV, só na GovPass...). */
    blockedSelection: Function as PropType<
      (v: VeiculoAnttPayload) => string | boolean
    >,
    /** IMF: a placa é de outro proprietário que não o da inclusão? */
    outroProprietario: Function as PropType<(v: VeiculoAnttPayload) => unknown>
  },
  emits: ["buscar", "confirm-selection", "cancel-selection", "abrir-cadastro"]
});
</script>

<style scoped lang="scss">
.rntrc-aviso {
  padding: $space-3 $space-4;
  border-radius: $radius-md;
  border: 1px solid $border-color-light;
  font-size: $font-size-sm;

  &--info {
    background: $info-light;
    border-left: 4px solid $info;
  }
}

.rntrc-busca-limite {
  color: $text-secondary;
}
</style>
